import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { useTranslation } from "react-i18next";

export const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || "";

const SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const SCRIPT_TIMEOUT_MS = 10000;
const SUPPORTED_LANGUAGES = ["tr", "en", "ru"];

let scriptPromise = null;

const loadTurnstileScript = () => {
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve(window.turnstile);
      return;
    }

    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;

    const timeoutId = setTimeout(() => {
      scriptPromise = null;
      reject(new Error("turnstile script timeout"));
    }, SCRIPT_TIMEOUT_MS);

    script.onload = () => {
      clearTimeout(timeoutId);
      if (window.turnstile) {
        resolve(window.turnstile);
      } else {
        reject(new Error("turnstile unavailable"));
      }
    };

    script.onerror = () => {
      clearTimeout(timeoutId);
      scriptPromise = null;
      reject(new Error("turnstile script failed"));
    };

    document.head.appendChild(script);
  });

  return scriptPromise;
};

const TurnstileWidget = forwardRef(function TurnstileWidget(
  { action, onVerify, onExpire, onError, onLoadError },
  ref,
) {
  const { i18n } = useTranslation();
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  const detected = (i18n.language || "").split("-")[0];
  const language = SUPPORTED_LANGUAGES.includes(detected) ? detected : "auto";

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
      }
    },
  }));

  useEffect(() => {
    let cancelled = false;

    if (widgetIdRef.current !== null) return undefined;

    loadTurnstileScript()
      .then((turnstile) => {
        if (
          cancelled ||
          !containerRef.current ||
          widgetIdRef.current !== null
        ) {
          return;
        }

        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          action,
          language,
          appearance: "interaction-only",
          "refresh-expired": "auto",
          callback: (token) => onVerify?.(token),
          "expired-callback": () => onExpire?.(),
          "error-callback": () => onError?.(),
        });
      })
      .catch(() => {
        if (!cancelled) onLoadError?.();
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [language]);

  return <div ref={containerRef} />;
});

export default TurnstileWidget;
